<template>
  <header class="site-header">
    <a class="brand" href="/" :aria-label="language.code === 'de' ? 'GuildGuardian Startseite' : 'GuildGuardian homepage'">
      <MascotLogo />
      <span class="brand-copy">
        <strong>GuildGuardian</strong>
        <small>Discord Bot</small>
      </span>
    </a>

    <nav class="main-nav" :aria-label="language.code === 'de' ? 'Hauptnavigation' : 'Main navigation'">
      <template v-for="item in navigation" :key="item.label">
        <button
          v-if="item.href === '#resources'"
          class="nav-dropdown-trigger"
          type="button"
          :aria-expanded="isResourcesOpen.toString()"
          aria-controls="resources-menu"
          @click="toggleResources"
        >
          <AppIcon v-if="item.icon" :name="item.icon" />
          <span>{{ item.label }}</span>
          <AppIcon name="chevron" />
        </button>
        <a v-else :href="item.href">
          <AppIcon v-if="item.icon" :name="item.icon" />
          <span>{{ item.label }}</span>
        </a>
      </template>
    </nav>

    <div v-if="isResourcesOpen" id="resources-menu" class="resources-menu">
      <section v-for="column in resources.columns" :key="column.title">
        <h2>{{ column.title }}</h2>
        <a v-for="item in column.items" :key="item.title" :href="item.href" class="resource-link">
          <span class="resource-icon">
            <AppIcon :name="item.icon" />
          </span>
          <span>
            <strong>
              {{ item.title }}
              <em v-if="item.badge">{{ item.badge }}</em>
            </strong>
            <small>{{ item.copy }}</small>
          </span>
        </a>
      </section>
    </div>

    <div class="header-tools">
      <div class="language-menu-wrap">
        <button
          class="language-button"
          type="button"
          :aria-label="language.code === 'de' ? 'Sprache wechseln' : 'Change language'"
          :aria-expanded="isLanguageOpen.toString()"
          aria-controls="language-menu"
          @click="toggleLanguage"
        >
          <span class="language-flag" :class="`language-flag--${language.code}`"></span>
          <span>{{ language.shortLabel }}</span>
          <AppIcon name="chevron" />
        </button>

        <div v-if="isLanguageOpen" id="language-menu" class="language-menu">
          <button
            v-for="option in languages"
            :key="option.code"
            class="language-option"
            :class="{ 'language-option--active': option.code === language.code }"
            type="button"
            @click="selectLanguage(option.code)"
          >
            <span class="language-flag" :class="`language-flag--${option.code}`"></span>
            <span>
              <strong>{{ option.label }}</strong>
              <small>{{ option.shortLabel }}</small>
            </span>
          </button>
        </div>
      </div>
      <button
        class="theme-button"
        type="button"
        :aria-label="theme.label"
        :title="theme.label"
        @click="$emit('theme-change')"
      >
        <AppIcon :name="theme.icon" />
      </button>
      <template v-for="action in actions" :key="action.label">
        <button
          v-if="action.variant === 'discord'"
          class="discord-auth-button"
          :class="{ 'discord-auth-button--connected': discordAccount }"
          type="button"
          @click="discordAccount ? disconnectDiscord() : connectDiscord()"
        >
          <img
            v-if="discordAccount && discordAccount.avatarUrl"
            :src="discordAccount.avatarUrl"
            :alt="discordAccount.username"
          />
          <span v-else-if="discordAccount" class="discord-auth-button__avatar">
            {{ discordAccountInitial }}
          </span>
          <AppIcon v-else :name="action.icon" />
          <span v-if="!discordAccount">{{ action.label }}</span>
          <span v-else class="discord-auth-button__account">
            <strong>{{ discordAccount.handle }}</strong>
            <small>ID {{ discordAccount.userId || discordAccount.id }}</small>
          </span>
        </button>
        <a
          v-else
          class="action-button"
          :class="`action-button--${action.variant}`"
          :href="action.href"
        >
          <AppIcon v-if="action.icon" :name="action.icon" />
          <span>{{ action.label }}</span>
        </a>
      </template>
    </div>
  </header>
</template>

<script>
import AppIcon from './AppIcon.vue'
import MascotLogo from './MascotLogo.vue'
import {
  connectDiscordAccount,
  disconnectDiscordAccount,
  getConnectedDiscordAccount
} from '../services/applicationPortalService'

const discordAccountChangedEvent = 'guildguardian-discord-account-changed'

export default {
  name: 'SiteHeader',
  components: {
    AppIcon,
    MascotLogo
  },
  emits: ['language-change', 'theme-change'],
  props: {
    navigation: {
      type: Array,
      required: true
    },
    actions: {
      type: Array,
      required: true
    },
    language: {
      type: Object,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    resources: {
      type: Object,
      required: true
    },
    theme: {
      type: Object,
      required: true
    },
    themes: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      discordAccount: getConnectedDiscordAccount(),
      isResourcesOpen: false,
      isLanguageOpen: false
    }
  },
  mounted() {
    window.addEventListener('click', this.closeResourcesOnOutsideClick)
    window.addEventListener(discordAccountChangedEvent, this.updateDiscordAccount)
  },
  beforeUnmount() {
    window.removeEventListener('click', this.closeResourcesOnOutsideClick)
    window.removeEventListener(discordAccountChangedEvent, this.updateDiscordAccount)
  },
  computed: {
    discordAccountInitial() {
      return (this.discordAccount?.username || this.discordAccount?.handle || 'D').charAt(0).toUpperCase()
    }
  },
  methods: {
    connectDiscord() {
      this.discordAccount = connectDiscordAccount()
    },
    disconnectDiscord() {
      disconnectDiscordAccount()
      this.discordAccount = null
    },
    updateDiscordAccount(event) {
      this.discordAccount = event.detail || getConnectedDiscordAccount()
    },
    toggleResources() {
      this.isResourcesOpen = !this.isResourcesOpen
      this.isLanguageOpen = false
    },
    toggleLanguage() {
      this.isLanguageOpen = !this.isLanguageOpen
      this.isResourcesOpen = false
    },
    selectLanguage(locale) {
      this.isLanguageOpen = false
      this.$emit('language-change', locale)
    },
    closeResourcesOnOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isResourcesOpen = false
        this.isLanguageOpen = false
      }
    }
  }
}
</script>
